// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React from 'react';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info, HelpCircle } from 'lucide-react';

/**
 * Editorial Modal Component
 * Beautiful, artisanal modals that match the app's design system
 *
 * Types:
 * - alert: Simple message with OK button
 * - confirm: Question with Cancel/Confirm buttons
 * - warning: Warning message
 * - error: Error message
 * - success: Success message
 * - info: Information message
 */

export function Modal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmText,
  cancelText,
  language = 'en'
}) {
  if (!isOpen) return null;

  const isConfirm = type === 'confirm';

  // Icon and color configuration
  const typeConfig = {
    alert: {
      icon: AlertCircle,
      gradient: 'from-terracotta to-terracotta-dark',
      iconBg: 'bg-terracotta/20',
      iconColor: 'text-terracotta',
      primaryBtn: 'bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] border border-[var(--btn-primary-border)] hover:bg-[var(--btn-primary-hover-bg)]'
    },
    confirm: {
      icon: HelpCircle,
      gradient: 'from-sage to-sage-dark',
      iconBg: 'bg-sage/20',
      iconColor: 'text-sage-dark',
      primaryBtn: 'bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] border border-[var(--btn-primary-border)] hover:bg-[var(--btn-primary-hover-bg)]'
    },
    warning: {
      icon: AlertTriangle,
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-[var(--status-warning-soft)]',
      iconColor: 'text-[var(--status-warning)]',
      primaryBtn: 'bg-[var(--btn-ghost-bg)] text-[var(--btn-ghost-text)] border border-[var(--btn-ghost-border)] hover:bg-[var(--btn-ghost-hover-bg)]'
    },
    error: {
      icon: AlertCircle,
      gradient: 'from-red-500 to-red-600',
      iconBg: 'bg-[var(--status-danger-soft)]',
      iconColor: 'text-[var(--status-danger)]',
      primaryBtn: 'bg-[var(--btn-danger-bg)] text-[var(--btn-danger-text)] border border-[var(--btn-danger-border)] hover:bg-[var(--btn-danger-hover-bg)]'
    },
    success: {
      icon: CheckCircle,
      gradient: 'from-sage to-sage-dark',
      iconBg: 'bg-sage-light/30',
      iconColor: 'text-sage-dark',
      primaryBtn: 'bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-text)] border border-[var(--btn-secondary-border)] hover:bg-[var(--btn-secondary-hover-bg)]'
    },
    info: {
      icon: Info,
      gradient: 'from-terracotta to-terracotta-dark',
      iconBg: 'bg-terracotta/10',
      iconColor: 'text-terracotta',
      primaryBtn: 'bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] border border-[var(--btn-primary-border)] hover:bg-[var(--btn-primary-hover-bg)]'
    }
  };

  const config = typeConfig[type] || typeConfig.info;
  const Icon = config.icon;

  const defaultConfirmText = language === 'fr' ? 'Confirmer' : 'Confirm';
  const defaultCancelText = language === 'fr' ? 'Annuler' : 'Cancel';
  const defaultOkText = language === 'fr' ? 'OK' : 'OK';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm"
        onClick={onClose}
        style={{ animation: 'fadeIn 0.2s ease-out' }}
      />

      {/* Modal Content */}
      <div
        className="relative rounded-3xl shadow-lifted max-w-md w-full overflow-hidden"
        style={{ background: 'var(--surface-elevated)', animation: 'modalSlideUp 0.3s ease-out' }}
      >
        {/* Decorative header gradient */}
        <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--action-ghost-hover)] transition-colors z-10"
          aria-label={language === 'fr' ? 'Fermer' : 'Close'}
        >
          <X size={20} className="text-[var(--text-secondary)]" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl ${config.iconBg} flex items-center justify-center mb-6 mx-auto`}>
            <Icon size={32} className={config.iconColor} strokeWidth={2} />
          </div>

          {/* Title */}
          {title && (
            <h2 className="font-display text-2xl font-bold text-[var(--text-primary)] text-center mb-3 leading-tight">
              {title}
            </h2>
          )}

          {/* Message */}
          <p className="font-body-serif text-base text-[var(--text-secondary)] text-center leading-relaxed mb-8">
            {message}
          </p>

          {/* Buttons */}
          <div className={`flex gap-3 ${isConfirm ? 'flex-row' : 'flex-col'}`}>
            {isConfirm && (
              <button
                onClick={onClose}
                className="flex-1 bg-[var(--btn-ghost-bg)] text-[var(--btn-ghost-text)] border border-[var(--btn-ghost-border)] hover:bg-[var(--btn-ghost-hover-bg)] py-3 px-6 rounded-full font-ui font-semibold transition-all hover:shadow-soft"
              >
                {cancelText || defaultCancelText}
              </button>
            )}
            <button
              onClick={() => {
                if (onConfirm) onConfirm();
                onClose();
              }}
              className={`${isConfirm ? 'flex-1' : 'w-full'} ${config.primaryBtn} py-3 px-6 rounded-full font-ui font-semibold transition-all`}
            >
              {isConfirm ? (confirmText || defaultConfirmText) : (confirmText || defaultOkText)}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Hook for managing modal state
 * Usage:
 * const { showModal, openModal, closeModal } = useModal();
 */
export function useModal() {
  const [modalState, setModalState] = React.useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
    confirmText: '',
    cancelText: ''
  });

  const openModal = ({ type = 'info', title = '', message, onConfirm, confirmText, cancelText }) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  return {
    modalState,
    openModal,
    closeModal,
    showModal: (props) => (
      <Modal
        {...modalState}
        {...props}
        isOpen={modalState.isOpen}
        onClose={closeModal}
      />
    )
  };
}

export default Modal;
